/**
 * Версии и описание object stores Dexie.
 *
 * Строка индексов в формате Dexie: первый токен — первичный ключ
 * (`++id` = автоинкремент), далее — индексируемые поля (`&` = уникальный).
 */

export const DB_NAME = 'pos-system'

/** Текущая версия схемы. Инкрементировать при изменении stores. */
export const DB_VERSION = 17

/**
 * Описание stores версии 2.
 *
 * Изменения относительно v1:
 * - products: убран уникальный индекс на barcode (один и тот же товар может
 *   присутствовать в разных версиях каталога → штрихкод больше не уникален
 *   глобально). Добавлены составные индексы [catalogVersionId+barcode] и
 *   catalogVersionId для быстрой выборки активной версии.
 * - новый store catalogVersions — реестр версий каталога.
 * - новый store meta — служебные пары ключ/значение (активная версия и т.п.).
 */
export const SCHEMA: Record<string, string> = {
  // Товары: привязаны к версии каталога. Поиск по версии и по (версия+штрихкод).
  products: '++id, catalogVersionId, [catalogVersionId+barcode], barcode, gtin, name, updatedAt',
  // Версии каталога: выборка по дате.
  catalogVersions: '++id, &versionName, createdAt',
  // Служебные пары ключ/значение. key — первичный ключ (БЕЗ &: иначе Dexie
  // создаёт избыточный уникальный индекс на PK → ConstraintError на iOS WebKit).
  meta: 'key',
  // Чеки: поиск по бизнес-id, статусу и дате.
  receipts: '++id, &receiptId, status, createdAt',
  // Оффлайн-очередь: выборка по типу/статусу/дате.
  offlineQueue: '++id, type, status, createdAt',
  // Бэкапы (web-фолбэк): последний по дате.
  backups: '++id, createdAt'
}

/**
 * Описание stores версии 3 (облачная синхронизация).
 *
 * Добавлено относительно v2:
 * - cashiers — локальный справочник кассиров с PIN-хэшами для оффлайн-входа
 *   (PULL из облака /auth/pos/pins).
 * - sales_queue — оффлайн-очередь чеков для PUSH в облако (/sync/receipts),
 *   с флагом is_synced для фильтрации неотправленных.
 * Остальные stores без изменений.
 */
export const SCHEMA_V3: Record<string, string> = {
  products: '++id, catalogVersionId, [catalogVersionId+barcode], barcode, gtin, name, updatedAt',
  catalogVersions: '++id, &versionName, createdAt',
  meta: 'key',
  receipts: '++id, &receiptId, status, createdAt',
  offlineQueue: '++id, type, status, createdAt',
  backups: '++id, createdAt',
  // Справочник кассиров для оффлайн-PIN: первичный ключ user_id (БЕЗ &), индекс по имени.
  cashiers: 'user_id, name',
  // Оффлайн-очередь чеков для облака: UUID-ключ id (БЕЗ &), индекс по флагу синхронизации.
  sales_queue: 'id, is_synced, sold_at, device_id'
}

/**
 * Описание stores версии 4 (фикс iOS WebKit ConstraintError).
 *
 * Гарантированно накатывает ЧИСТЫЕ ключи без избыточного `&` на первичных
 * ключах (meta.key, cashiers.user_id, sales_queue.id). Нужна для устройств,
 * где база уже упала на старой схеме. Остальные stores — как в v2/v3.
 */
export const SCHEMA_V4: Record<string, string> = {
  products: '++id, catalogVersionId, [catalogVersionId+barcode], barcode, gtin, name, updatedAt',
  catalogVersions: '++id, &versionName, createdAt',
  meta: 'key',
  receipts: '++id, &receiptId, status, createdAt',
  offlineQueue: '++id, type, status, createdAt',
  backups: '++id, createdAt',
  cashiers: 'user_id, name',
  sales_queue: 'id, is_synced, sold_at, device_id'
}

/**
 * Описание stores версии 5 (кассовые смены).
 *
 * Добавлен store shifts для жизненного цикла смены (open → close, Z-отчёт).
 * Остальные stores как в v4.
 */
export const SCHEMA_V5: Record<string, string> = {
  ...SCHEMA_V4,
  shifts: '++id, shiftNumber, cashierId, status, openedAt, closedAt'
}

/**
 * Описание stores версии 6 (категории и весовые товары FusionPOS).
 *
 * products: добавлены индексы category и составной [catalogVersionId+category]
 * для быстрой навигации по категориям на кассе. Остальные stores как в v5.
 */
export const SCHEMA_V6: Record<string, string> = {
  ...SCHEMA_V5,
  products:
    '++id, catalogVersionId, [catalogVersionId+barcode], [catalogVersionId+category], category, barcode, gtin, name, updatedAt'
}

/**
 * Описание stores версии 7 (вскрытие тары / пул массы маркированных товаров).
 *
 * Добавлен store opened_containers — вскрытые головы сыра/колбасы с остатком
 * массы и привязанным кодом DataMatrix. Остальные stores как в v6.
 */
export const SCHEMA_V7: Record<string, string> = {
  ...SCHEMA_V6,
  opened_containers: '++id, barcode, status, openedAt'
}

/**
 * Описание stores версии 8 (заявки с касс, ARCH-TASK-58.1).
 *
 * products: индексы clientUuid (ключ заявки с кассы) и cloudId (id товара в
 * облаке) — стабильный матчинг при пулле каталога. Составной индекс
 * [catalogVersionId+barcode] НЕ уникален, поэтому товары без штрихкода по нему
 * не ищем. Остальные stores как в v7.
 */
export const SCHEMA_V8: Record<string, string> = {
  ...SCHEMA_V7,
  products:
    '++id, catalogVersionId, [catalogVersionId+barcode], [catalogVersionId+category], category, barcode, gtin, name, clientUuid, cloudId, updatedAt'
}

/**
 * Описание stores версии 9 (возвраты, ARCH-TASK-65.5).
 *
 * receipts: индекс originalReceiptId — быстрый поиск возвратов по исходному
 * чеку (лимит частичного возврата считается накопительно). Остальные stores
 * как в v8; data-миграция не требуется.
 */
export const SCHEMA_V9: Record<string, string> = {
  ...SCHEMA_V8,
  receipts: '++id, &receiptId, status, createdAt, originalReceiptId'
}

/**
 * Описание stores версии 10 (складской ledger и списания, ARCH-TASK-65.10).
 *
 * - stock_movements — источник истины по остаткам: продажа/возврат/списание/
 *   отмена/инвентаризация. `Product.stock` остаётся производным кэшем.
 * - writeoffs — документы списания (в т.ч. «возврат + списание»).
 * - receipts: индекс czStatus — быстрый поиск held-чеков (65.12).
 *
 * Чистое добавление stores/индексов; data-миграция не требуется.
 */
export const SCHEMA_V10: Record<string, string> = {
  ...SCHEMA_V9,
  receipts: '++id, &receiptId, status, createdAt, originalReceiptId, czStatus',
  stock_movements: '++id, &movementId, type, barcode, receiptId, writeoffId, createdAt',
  writeoffs: '++id, &writeoffId, receiptId, reasonCode, createdAt'
}

/**
 * Описание stores версии 11 (очередь синка ledger, ARCH-TASK-65.10).
 *
 * `syncState`: 0 — ждёт отправки, 1 — отправлено, 2 — отклонено облаком.
 * Индекс нужен, чтобы не читать весь ledger на каждом синке.
 */
export const SCHEMA_V11: Record<string, string> = {
  ...SCHEMA_V10,
  stock_movements:
    '++id, &movementId, type, barcode, receiptId, writeoffId, createdAt, syncState',
  writeoffs: '++id, &writeoffId, receiptId, reasonCode, createdAt, syncState'
}

/**
 * Описание stores версии 12 (замена non_fiscal → fiscal, 65.14).
 *
 * receipts: индексы replacementState, replacedByReceiptId и trash — выборка
 * задач на кассе и скрытие заменённых чеков из активных списков. Поля
 * tombstone (`voidedAt`, `replacesReceiptId`) живут в документе и не требуют
 * отдельных индексов.
 */
export const SCHEMA_V12: Record<string, string> = {
  ...SCHEMA_V11,
  receipts:
    '++id, &receiptId, status, createdAt, originalReceiptId, czStatus, replacementState, replacedByReceiptId, trash'
}

/** v13: смена в чеке — выборка документов смены для сверки с ККТ. */
export const SCHEMA_V13: Record<string, string> = {
  ...SCHEMA_V12,
  receipts:
    '++id, &receiptId, status, createdAt, originalReceiptId, czStatus, replacementState, replacedByReceiptId, trash, shiftNumber'
}

/** v14: подкатегории каталога — навигация «категория → подкатегория». */
export const SCHEMA_V14: Record<string, string> = {
  ...SCHEMA_V13,
  products:
    '++id, catalogVersionId, [catalogVersionId+barcode], [catalogVersionId+category], [catalogVersionId+category+subcategory], category, subcategory, barcode, gtin, name, clientUuid, cloudId, updatedAt'
}

/**
 * v15: дерево категорий.
 *
 * - новый store `categories` — верхний уровень (`parentName = null`) и листья;
 * - products: `categoryParent` — денормализованный верхний уровень и составной
 *   индекс `[catalogVersionId+categoryParent+category]` для фильтра кассы.
 * `subcategory` остаётся для совместимости, но UI его не использует.
 */
export const SCHEMA_V15: Record<string, string> = {
  ...SCHEMA_V14,
  products:
    '++id, catalogVersionId, [catalogVersionId+barcode], [catalogVersionId+category], [catalogVersionId+categoryParent+category], category, categoryParent, subcategory, barcode, gtin, name, clientUuid, cloudId, updatedAt',
  categories: '++id, name, parentName, [parentName+name], updatedAt'
}

/**
 * v16: порядковый номер товара в категории и флаг активности.
 *
 * - `categoryOrdinal` — устойчивый номер для плитки товара;
 * - `isActive` — деактивация без удаления (номер скрывается, но сохраняется);
 * - составной индекс для сортировки плиток внутри категории.
 */
export const SCHEMA_V16: Record<string, string> = {
  ...SCHEMA_V15,
  products:
    '++id, catalogVersionId, [catalogVersionId+barcode], [catalogVersionId+category], [catalogVersionId+categoryParent+category], [catalogVersionId+categoryParent+category+categoryOrdinal], category, categoryParent, categoryOrdinal, isActive, subcategory, barcode, gtin, name, clientUuid, cloudId, updatedAt'
}

/**
 * v17: бинарные ассеты (загруженные картинки категорий и товаров).
 * Ключ `category:<name>` / `product:<id>` уникален.
 */
export const SCHEMA_V17: Record<string, string> = {
  ...SCHEMA_V16,
  assets: '++id, &key, ownerType, updatedAt'
}

/** Ключ в store meta, хранящий id активной версии каталога. */
export const META_ACTIVE_VERSION = 'activeCatalogVersionId'

/** Ключ в store meta: разрешено ли кассирам заводить товары «на лету». */
export const META_ALLOW_ON_THE_FLY = 'allowOnTheFlyCreation'

/**
 * Ключ в store meta: device-token кассы.
 *
 * Не admin-JWT: только sync/чеки/проверка ЧЗ/каталог. Admin-токен живёт
 * в памяти/sessionStorage (см. `core/auth/cloudSession.ts`).
 */
export const META_DEVICE_TOKEN = 'device_token'

/** @deprecated Используйте `META_DEVICE_TOKEN`. Оставлено для миграции/чтения. */
export const META_AUTH_TOKEN = META_DEVICE_TOKEN

/** Старый ключ admin-JWT; удаляется при старте, чтобы не оставлять его в Dexie. */
export const META_LEGACY_AUTH_TOKEN = 'auth_token'

/** Ключ в store meta: tenant_id организации. */
export const META_TENANT_ID = 'tenant_id'

/** Ключ в store meta: идентификатор устройства/терминала. */
export const META_DEVICE_ID = 'device_id'

/**
 * Ключ в store meta: зона (рабочее место/отдел магазина).
 * Пул вскрытой тары общий для всех устройств одной зоны: терминал и телефон
 * видят одни и те же вскрытые головы. Зона приходит при сопряжении и НЕ
 * подменяет собой device_id (у каждого устройства он свой).
 */
export const META_ZONE_ID = 'zone_id'

/** Ключ в store meta: номер последней полученной версии каталога из облака. */
export const META_CATALOG_VERSION = 'catalog_version'

/**
 * Ключ в store meta: server_time последней успешной дельты каталога (ISO).
 * Основа time-based синхронизации (ARCH-TASK-59): следующий запрос просит
 * изменения строго после этой метки.
 */
export const META_CATALOG_SYNCED_AT = 'catalog_synced_at'

/**
 * Ключ в store meta: метка выполненного разового полного пересинка каталога
 * (ARCH-TASK-61.0c). Нужен, чтобы устройства один раз перечитали каталог
 * после исправления матчинга удалённых строк.
 */
export const META_CATALOG_RESYNC = 'catalog_resync_marker'

/**
 * Ключ в store meta: очередь цен, изменённых в админке, но ещё не отправленных
 * в облако (ARCH-TASK-60.3). Формат: [{ cloudId, price, at }].
 */
export const META_PENDING_PRICES = 'pending_prices'


/** Ключ в store meta: валюта магазина ('RUB' | 'UZS'). */
export const META_STORE_CURRENCY = 'store_currency'
/** Порог «товар заканчивается» для «Обзора» (в единицах товара, ARCH-TASK-62c). */
export const META_LOW_STOCK_THRESHOLD = 'lowStockThreshold'
/** Ключ в store meta: показывать ли на кассе блок «Часто покупают». */
export const META_FREQUENT_PRODUCTS_ENABLED = 'pos_frequent_products_enabled'

/** Ключ в store meta: устройство само было сопряжено через pair_token (BYOD). */
export const META_DEVICE_PAIRED = 'device_paired'

/**
 * Ключ в store meta: durable-очередь флагов модулей, ещё не отправленных
 * в `tenants.settings.modules`. Переживает перезагрузку кассы.
 */
export const META_MODULES_PENDING = 'modulesPending'

/** Ключ в store meta: id активной (открытой) кассовой смены. */
export const META_ACTIVE_SHIFT_ID = 'activeShiftId'

/** Ключ в store meta: режим банковского терминала ('manual' | 'integrated'). */
export const META_CARD_TERMINAL_MODE = 'card_terminal_mode'

/** Кэш ролевой раскладки админки (облако — источник истины). */
export const META_ROLE_NAVIGATION = 'role_navigation'



/** Ключ в store meta: название организации (для шапки админки). */
export const META_COMPANY_NAME = 'company_name'
export const META_COMPANY_INN = 'company_inn'
export const META_STORE_ADDRESS = 'store_address'
export const META_STORE_ID = 'store_id'

/**
 * Ключ meta: выбор кассира «фискализировать наличные» в рамках смены.
 * Локальное UI-предпочтение (в облако не уходит — смена общая на зону);
 * при перезагрузке кассы выбор восстанавливается, на новой смене — сброс.
 */
export const fiscalPrefKey = (zoneId: string, shiftNumber: number) =>
  `fiscalPref:${zoneId}:${shiftNumber}`
